// ── Support-Diagnose ───────────────────────────────────────────────────
// Baut ein Diagnose-Paket für Support-Anfragen: alle Transaktionen + der
// technische Kontext, den wir laut Praxis IMMER zuerst brauchen (TX-Anzahl,
// Origin/URL, Bundle-Hash, Plattform, Plan, Sprache/Währung).
//
// Wir haben per Design KEINEN Zugriff auf fremde Profile (Privacy-first, kein
// Tracking). Der User reicht uns die Daten freiwillig per E-Mail-Anhang — damit
// lässt sich jeder Daten-/Berechnungs-Bug deterministisch lokal reproduzieren.
//
// SICHERHEIT: Hier landen NIEMALS Secrets — keine API-Keys, keine
// verschlüsselten Börsen-Credentials, kein Passwort, keine UID. Nur die
// Transaktionsliste (der eigentliche Bug-Input) plus harmloser Tech-Kontext.

const MAIN_RE = /\/static\/js\/main\.([\w-]+)\.js/;

// Der Hash des laufenden main-Bundles IST die Version (siehe useVersionCheck).
function runningBundleId() {
  if (typeof document === 'undefined') return null;
  for (const s of document.querySelectorAll('script[src]')) {
    const m = (s.getAttribute('src') || '').match(MAIN_RE);
    if (m) return m[1];
  }
  return null;
}

function readLS(key) {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

/**
 * Baut das Diagnose-Paket.
 * @returns {{ payload: object, summary: object }}
 *   payload = die vollständige JSON, die heruntergeladen wird
 *   summary = die paar Zahlen, die zusätzlich in den Mail-Text geschrieben werden
 */
export function buildDiagnostics({ txs = [], tier = null, platform = 'web' } = {}) {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const loc = typeof window !== 'undefined' ? window.location : {};
  const generatedAt = new Date().toISOString();
  const list = Array.isArray(txs) ? txs : [];

  // Kompakte Übersicht fürs Mail-Body (auch nützlich, falls der Anhang vergessen
  // wird — dann haben wir wenigstens die Triage-Zahlen).
  const summary = {
    generatedAt,
    txCount: list.length,
    origin: loc.origin || '—',
    bundleId: runningBundleId() || 'dev',
    platform,
    tier: tier || 'free',
    lang: readLS('red_lang') || 'de',
    currency: readLS('red_currency') || 'EUR',
  };

  const payload = {
    _kind: 'red-diagnostics',
    _schema: 1,
    meta: {
      ...summary,
      href: loc.href || '—',
      userAgent: nav.userAgent || '—',
      language: nav.language || '—',
      screen:
        typeof window !== 'undefined' && window.screen
          ? { w: window.screen.width, h: window.screen.height, dpr: window.devicePixelRatio || 1 }
          : null,
      lastError: readLS('red_last_error') || null,
    },
    txs: list,
  };

  return { payload, summary };
}

/**
 * Löst den Datei-Download der Diagnose-JSON aus.
 * @returns {string} der Dateiname (für die Anhang-Aufforderung im Mail-Text)
 */
export function downloadDiagnostics(payload) {
  const fileName = `red-diagnose-${new Date().toISOString().slice(0, 10)}.json`;
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Blob-URL nach dem Klick wieder freigeben (Browser hat den Download da schon angestoßen).
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return fileName;
}
